import {
  IonSearchbar,
  IonButton,
  IonSpinner,
  IonText,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonModal,
  useIonRouter,
} from '@ionic/react';
import { useState } from 'react';
import { useMutation } from '@apollo/client/react';
import { SEARCH_FANTASY_LEAGUES } from '../../../graphql/queries/searchFantasyLeagues';
import { JOIN_FANTASY_LEAGUE } from '../../../graphql/mutations/joinFantasyLeague';
import { useInfiniteScrollQuery } from '../../../hooks/useInfiniteScrollQuery';
import { SecretCodeModal } from './SecretCodeModal';
import { JoinLeagueModal } from './JoinLeagueModal';
import type { OfficialChannel } from './OfficialChannelsFollowBlock';
import { useFeatureFlags } from '../../../hooks/useFeatureFlags';
import { FeatureFlag } from '../../../types/featureFlags';
import { GET_MY_FANTASY_LEAGUES } from '../../../graphql/queries/myFantasyLeagues';
import { FantasyLeagueCard } from './fantasy-league-card/FantasyLeagueCard';
import { mapSearchLeagueToCardData } from './fantasy-league-card/mappers';
import type {
  SearchFantasyLeaguesQuery,
  SearchFantasyLeaguesQueryVariables,
  JoinFantasyLeagueMutation,
  JoinFantasyLeagueMutationVariables,
} from '../../../types/graphql-generated';
import {
  LEAGUE_SEARCH_PAGE_SIZE,
  LEAGUE_SEARCH_MIN_TERM_LENGTH,
} from '../../../appConfig';

export const LeagueSearchContent = () => {
  const router = useIonRouter();
  const { isEnabled } = useFeatureFlags();
  const [searchTerm, setSearchTerm] = useState('');
  // Term currently applied to the query. An empty string lists the public
  // leagues, which is what we show when the user has not searched anything.
  const [activeSearchTerm, setActiveSearchTerm] = useState('');
  // League the user is about to join. While set, the join screen is open.
  const [selectedLeague, setSelectedLeague] = useState<{
    id: string;
    name: string;
    isPublic: boolean;
    officialChannels: OfficialChannel[];
  } | null>(null);
  const [joinError, setJoinError] = useState<string | null>(null);

  const { data, loading, error, loadMore, refetch, resetPagination } = useInfiniteScrollQuery<
    SearchFantasyLeaguesQuery,
    SearchFantasyLeaguesQueryVariables
  >({
    query: SEARCH_FANTASY_LEAGUES,
    buildVariables: (offset) => ({ name: activeSearchTerm, limit: LEAGUE_SEARCH_PAGE_SIZE, offset }),
    pageSize: LEAGUE_SEARCH_PAGE_SIZE,
    append: (previous, next) => ({
      searchFantasyLeagues: {
        ...next.searchFantasyLeagues,
        leagues: [
          ...previous.searchFantasyLeagues.leagues,
          ...next.searchFantasyLeagues.leagues,
        ],
      },
    }),
  });

  const [joinLeagueMutation, { loading: joiningLeague }] = useMutation<
    JoinFantasyLeagueMutation,
    JoinFantasyLeagueMutationVariables
  >(JOIN_FANTASY_LEAGUE, {
    refetchQueries: [ { query: GET_MY_FANTASY_LEAGUES } ],
  });

  const searchResults = data?.searchFantasyLeagues.leagues || [];
  const totalCount = data?.searchFantasyLeagues.totalCount || 0;

  // Applies a term to the query. An empty term brings back the public leagues listing.
  const applySearchTerm = (term: string) => {
    setJoinError(null);
    setActiveSearchTerm(term);
    resetPagination();
    refetch({
      name: term,
      limit: LEAGUE_SEARCH_PAGE_SIZE,
      offset: 0,
    });
  };

  const handleSearchInput = (value: string) => {
    setSearchTerm(value);
    // Going back to an empty searchbar restores the public leagues listing
    if (value.trim().length === 0 && activeSearchTerm !== '') {
      applySearchTerm('');
    }
  };

  const handleSearch = () => {
    if (searchTerm.length >= LEAGUE_SEARCH_MIN_TERM_LENGTH) {
      applySearchTerm(searchTerm);
    }
  };

  const closeJoinModal = () => {
    setSelectedLeague(null);
    setJoinError(null);
  };

  const handleJoin = async (secretCode?: string, officialChannelIds?: string[]) => {
    if (!selectedLeague) return;

    setJoinError(null);

    try {
      const result = await joinLeagueMutation({
        variables: {
          input: {
            leagueId: selectedLeague.id,
            ...(secretCode !== undefined ? { secretCode } : {}),
            ...(officialChannelIds !== undefined ? { officialChannelIds } : {}),
          },
        },
      });

      const errors = result.data?.joinFantasyLeague?.errors;
      if (errors && errors.length > 0) {
        setJoinError(errors[0]);
        return;
      }

      setSelectedLeague(null);
      router.push(`/app/ligas/${selectedLeague.id}/actividad`);
    } catch {
      setJoinError('Error al unirse a la liga');
    }
  };

  const handleLeagueClick = (league: SearchFantasyLeaguesQuery['searchFantasyLeagues']['leagues'][number]) => {
    setJoinError(null);
    setSelectedLeague({
      id: league.id,
      name: league.name,
      isPublic: league.isPublic,
      // The follow block is only offered with the posts feature
      officialChannels: isEnabled(FeatureFlag.POSTS) ? (league.competition?.officialChannels ?? []) : [],
    });
  };

  const canSearch = searchTerm.length >= LEAGUE_SEARCH_MIN_TERM_LENGTH;
  const isBrowsingPublicLeagues = activeSearchTerm === '';
  const hasMoreResults = searchResults.length < totalCount;

  return (
    <>
      <div className="space-y-4">
        {/* Search Input */}
        <div className="space-y-2">
          <IonSearchbar
            value={searchTerm}
            onIonInput={(e) => handleSearchInput(e.detail.value ?? '')}
            onIonClear={() => handleSearchInput('')}
            placeholder="Buscar ligas..."
            debounce={0}
          />

          {searchTerm.length > 0 && searchTerm.length < LEAGUE_SEARCH_MIN_TERM_LENGTH && (
            <IonText color="medium">
              <p className="text-sm px-4">
                Escribe al menos {LEAGUE_SEARCH_MIN_TERM_LENGTH} caracteres para buscar
              </p>
            </IonText>
          )}

          <IonButton
            expand="block"
            onClick={handleSearch}
            disabled={!canSearch || loading}
          >
            {loading ? 'Buscando...' : 'Buscar'}
          </IonButton>
        </div>

        {/* Mutation Error */}
        {joinError && !selectedLeague && (
          <IonText color="danger">
            <p className="px-4">{joinError}</p>
          </IonText>
        )}

        {/* Loading Spinner */}
        {loading && searchResults.length === 0 && (
          <div className="flex justify-center items-center min-h-[200px]">
            <IonSpinner name="crescent" />
          </div>
        )}

        {/* Error Message */}
        {error && (
          <IonText color="danger">
            <p>Error al buscar ligas: {error.message}</p>
          </IonText>
        )}

        {/* Empty State - No public leagues available yet */}
        {isBrowsingPublicLeagues && !loading && !error && searchResults.length === 0 && (
          <div className="flex flex-col justify-center items-center min-h-[200px] text-center px-4">
            <IonText color="medium">
              <p className="text-lg">Todavía no hay ligas públicas</p>
              <p className="text-sm mt-2">
                Busca una liga por su nombre para unirte a ella
              </p>
            </IonText>
          </div>
        )}

        {/* Empty State - No Results */}
        {!isBrowsingPublicLeagues && !loading && !error && searchResults.length === 0 && (
          <div className="flex justify-center items-center min-h-[200px]">
            <IonText color="medium">
              <p>No se encontraron ligas con &quot;{activeSearchTerm}&quot;</p>
            </IonText>
          </div>
        )}

        {/* Results List */}
        {searchResults.length > 0 && (
          <>
            <div className="px-4">
              {isBrowsingPublicLeagues ? (
                <IonText>
                  <p className="text-sm font-semibold text-gray-800">Ligas públicas</p>
                  <p className="text-sm text-gray-600">
                    Únete a cualquiera de estas ligas abiertas, o busca una liga
                    concreta por su nombre.
                  </p>
                </IonText>
              ) : (
                <IonText>
                  <p className="text-sm text-gray-600">
                    {totalCount} {totalCount === 1 ? 'liga encontrada' : 'ligas encontradas'}
                  </p>
                </IonText>
              )}
            </div>

            <div className="pb-2">
              {searchResults.map((league) => {
                const cardData = mapSearchLeagueToCardData(league);
                return (
                  <FantasyLeagueCard
                    key={cardData.id}
                    league={cardData}
                    onAction={() => handleLeagueClick(league)}
                  />
                );
              })}
            </div>

            {/* Infinite Scroll */}
            {hasMoreResults && (
              <IonInfiniteScroll onIonInfinite={loadMore} threshold="100px">
                <IonInfiniteScrollContent
                  loadingSpinner="bubbles"
                  loadingText="Cargando más..."
                />
              </IonInfiniteScroll>
            )}
          </>
        )}
      </div>

      {/* Join screen: secret code for private leagues, confirmation for public ones */}
      <IonModal isOpen={selectedLeague !== null} onDidDismiss={closeJoinModal}>
        {selectedLeague &&
          (selectedLeague.isPublic ? (
            <JoinLeagueModal
              leagueName={selectedLeague.name}
              officialChannels={selectedLeague.officialChannels}
              onJoin={(officialChannelIds) => handleJoin(undefined, officialChannelIds)}
              onDismiss={closeJoinModal}
              isLoading={joiningLeague}
              error={joinError}
            />
          ) : (
            <SecretCodeModal
              leagueName={selectedLeague.name}
              officialChannels={selectedLeague.officialChannels}
              onJoin={handleJoin}
              onDismiss={closeJoinModal}
              isLoading={joiningLeague}
              error={joinError}
            />
          ))}
      </IonModal>
    </>
  );
};
